import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { AIService, GenerationContext, UnifiedPlaylistResponse } from '../../../core/services/ai.service';
import { PlaylistRefreshService } from '../../../core/services/playlist-refresh.service';
import { PlaylistGenerationSettingsService } from '../../../core/services/playlist-generation-settings.service';

export interface QuickPlaySettings {
  selectedGenres: string[];
  playlistLength: number;
  historyDays: number;
  playlistName: string;
  maximumPlayCount: number;
  popularity?: {
    min: number;
    max: number;
  };
  yearRange?: {
    min: number;
    max: number;
  };
}

export interface QuickPlayGenerateRequest {
  genre: string;
  playlistLength?: number;
  historyDays?: number;
  playlistName?: string;
  popularity?: {
    min?: number;
    max?: number;
  };
  yearRange?: {
    min?: number;
    max?: number;
  };
}

export interface QuickPlayGenerateResponse {
  tracks: any[];
  totalFound: number;
  afterFiltering: number;
  final: number;
}

export interface QuickPlayCreateResponse {
  playlist: any;
  playbackStarted: boolean;
}

@Injectable()
export class QuickPlayService {
  private readonly apiUrl = `${environment.apiUrl}/quick-play`;

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private aiService: AIService,
    private playlistRefreshService: PlaylistRefreshService,
    private playlistSettingsService: PlaylistGenerationSettingsService
  ) {}

  // Expose centralized settings as QuickPlaySettings format for backward compatibility
  get settings$(): Observable<QuickPlaySettings> {
    return this.playlistSettingsService.settings$.pipe(
      map(settings => settings ? {
        selectedGenres: settings.selectedGenres,
        playlistLength: settings.playlistLength,
        historyDays: settings.historyFilterDays,
        playlistName: settings.playlistName,
        maximumPlayCount: settings.maximumPlayCount,
        popularity: settings.popularity,
        yearRange: settings.yearRange
      } : this.getDefaultSettings())
    );
  }

  private getDefaultSettings(): QuickPlaySettings {
    return {
      selectedGenres: [],
      playlistLength: 30,
      historyDays: 7,
      playlistName: 'Dynamic Playlist',
      maximumPlayCount: 2,
      popularity: { min: 20, max: 90 }
    };
  }

  saveSettings(settings: QuickPlaySettings): void {
    // Update centralized settings instead of localStorage
    this.playlistSettingsService.updateSettings({
      selectedGenres: settings.selectedGenres,
      playlistLength: settings.playlistLength,
      historyFilterDays: settings.historyDays,
      playlistName: settings.playlistName,
      maximumPlayCount: settings.maximumPlayCount,
      popularity: settings.popularity,
      yearRange: settings.yearRange
    }).subscribe();
  }

  // Helper methods for quick updates
  updatePlaylistName(name: string): void {
    this.playlistSettingsService.updatePlaylistName(name).subscribe();
  }

  updatePlaylistLength(length: number): void {
    this.playlistSettingsService.updatePlaylistLength(length).subscribe();
  }

  updateSelectedGenres(genres: string[]): void {
    this.playlistSettingsService.updateSelectedGenres(genres).subscribe();
  }

  getSettings(): QuickPlaySettings {
    return this.playlistSettingsService.getCurrentSettings() ? {
      selectedGenres: this.playlistSettingsService.getCurrentSettings().selectedGenres,
      playlistLength: this.playlistSettingsService.getCurrentSettings().playlistLength,
      historyDays: this.playlistSettingsService.getCurrentSettings().historyFilterDays,
      playlistName: this.playlistSettingsService.getCurrentSettings().playlistName,
      maximumPlayCount: this.playlistSettingsService.getCurrentSettings().maximumPlayCount,
      popularity: this.playlistSettingsService.getCurrentSettings().popularity,
      yearRange: this.playlistSettingsService.getCurrentSettings().yearRange
    } : this.getDefaultSettings();
  }

  getAvailableGenres(): Observable<{ genres: string[] }> {
    // Return hardcoded genres since the backend endpoint was removed
    const genres = [
      'rock', 'pop', 'hip-hop', 'jazz', 'classical', 'electronic',
      'country', 'r&b', 'indie', 'blues', 'folk', 'reggae',
      'punk', 'metal', 'funk', 'disco', 'soul', 'gospel'
    ];
    return new Observable(observer => {
      observer.next({ genres });
      observer.complete();
    });
  }

  // Legacy methods removed - now use unified AIService endpoint

  async quickPlay(genre: string): Promise<{ playlist: any; generatedPlaylist: any }> {
    const settings = this.getSettings();
    this.loadingSubject.next(true);
    
    try {
      // Use unified AIService with quick_play context
      const context: GenerationContext = {
        type: 'quick_play',
        genre: genre,
        createPlaylist: true,
        playlistName: settings.playlistName,
        playlistDescription: `Dynamic ${genre} playlist created with Quick Play`,
        makePublic: false
      };
      
      const response = await this.aiService.generatePlaylist(context);
      
      this.loadingSubject.next(false);
      
      // Navigate to playlist details
      if (response.spotifyPlaylist?.id) {
        this.router.navigate(['/playlists', response.spotifyPlaylist.id]);
      }
      
      // Convert to legacy format for compatibility
      const legacyFormat = {
        id: `playlist_${Date.now()}`,
        name: response.generatedPlaylist.name,
        description: response.generatedPlaylist.description,
        tracks: response.generatedPlaylist.tracks
      };
      
      return {
        playlist: response.spotifyPlaylist,
        generatedPlaylist: legacyFormat
      };
    } catch (error) {
      this.loadingSubject.next(false);
      throw error;
    }
  }

  private buildAIPrompt(genre: string, settings: QuickPlaySettings): string {
    const prompts = [
      `Create a dynamic ${genre} playlist`,
      `with ${settings.playlistLength} tracks`,
      'that matches my current mood and energy level'
    ];
    
    if (settings.historyDays > 0 && settings.maximumPlayCount >= 0) {
      prompts.push(`avoiding tracks I've played more than ${settings.maximumPlayCount} time(s) in the last ${settings.historyDays} days`);
    }
    
    if (settings.popularity) {
      const { min, max } = settings.popularity;
      if (min > 30) {
        prompts.push('focusing on popular and well-known tracks');
      } else if (max < 50) {
        prompts.push('including hidden gems and underground favorites');
      }
    }
    
    if (settings.yearRange) {
      const { min, max } = settings.yearRange;
      if (min && max && min === max) {
        prompts.push(`from the year ${min}`);
      } else if (min && max) {
        prompts.push(`from ${min} to ${max}`);
      }
    }
    
    prompts.push('Ensure variety in artists and subgenres while maintaining a cohesive flow.');
    
    return prompts.join(' ');
  }
  
  // Create a brand-new dynamic playlist from a freeform prompt. Lets the AI
  // pick the playlist name and marks it dynamic on the backend so the user
  // can re-regenerate it later.
  async createNewDynamicPlaylist(prompt: string): Promise<{ playlistId: string; name: string } | null> {
    if (!prompt || !prompt.trim()) return null;
    this.loadingSubject.next(true);
    try {
      const context: GenerationContext = {
        type: 'user_input',
        userRequest: prompt.trim(),
        createPlaylist: true,
        createNew: true,
        // Omit playlistName so the AI-generated name is used.
        makePublic: false,
      };

      const response = await this.aiService.generatePlaylist(context);
      this.loadingSubject.next(false);

      if (!response.spotifyPlaylist?.id) {
        return null;
      }

      this.playlistRefreshService.triggerRefresh({
        playlistId: response.spotifyPlaylist.id,
        playlistName: response.generatedPlaylist.name
      });

      this.router.navigate(['/playlists', response.spotifyPlaylist.id]);

      return {
        playlistId: response.spotifyPlaylist.id,
        name: response.generatedPlaylist.name
      };
    } catch (error) {
      this.loadingSubject.next(false);
      throw error;
    }
  }

  async processVoiceCommand(transcript: string): Promise<void> {
    try {
      // Use unified AIService with user_input context (voice transcript processed as text input)
      const context: GenerationContext = {
        type: 'user_input',
        userRequest: transcript,
        createPlaylist: true,
        playlistName: 'Dynamic Playlist',
        playlistDescription: `Generated from voice command: "${transcript}"`,
        makePublic: false
      };
      
      const response = await this.aiService.generatePlaylist(context);
      
      // Navigate to the created playlist
      if (response.spotifyPlaylist?.id) {
        // Small delay to ensure Spotify has fully processed the playlist creation
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Trigger refresh event before navigation
        this.playlistRefreshService.triggerRefresh({
          playlistId: response.spotifyPlaylist.id,
          playlistName: 'Dynamic Playlist'
        });
        
        // Navigate to the playlist
        await this.router.navigate(['/playlists', response.spotifyPlaylist.id]);
        
        // Trigger another refresh after navigation to ensure the view is updated
        setTimeout(() => {
          if (response.spotifyPlaylist) {
            this.playlistRefreshService.triggerRefresh({
              playlistId: response.spotifyPlaylist.id,
              playlistName: 'Dynamic Playlist'
            });
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Voice command processing error:', error);
      throw error;
    }
  }

  private getTimeOfDay(): string {
    const hour = new Date().getHours();
    if (hour < 6) return 'night';
    if (hour < 12) return 'morning';
    if (hour < 18) return 'afternoon';
    return 'evening';
  }
}